import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../../api/client'
import { useOfflineFirstQuery } from '../../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../../lib/tenant'

type BillingSummary = {
  draft_claims: number
  scrubbed_claims: number
  submitted_claims: number
  denied_claims: number
  payments_count_mtd: number
  payments_amount_mtd: string
}

export default function BillingHomePage() {
  const tenantSlug = resolveTenantSlug()

  const cacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:billing:summary`
  }, [tenantSlug])

  const query = useOfflineFirstQuery<BillingSummary>({
    queryKey: ['billing', 'summary'],
    cacheKey,
    cacheMaxAgeMs: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<BillingSummary>('/api/v1/billing/summary/')
      return res.data
    },
  })

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
          <p className="text-sm text-gray-600">
            Claims are generated only from completed sessions with locked notes and valid authorization.
          </p>
        </div>
        <Link to="/app/billing/claims/generate" className="btn-primary">
          Generate claims
        </Link>
      </div>

      {query.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(query.error)}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {query.isLoading && !query.data ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="card p-6">
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="mt-3 h-8 w-16 bg-gray-200 rounded animate-pulse" />
            </div>
          ))
        ) : query.data ? (
          <>
            <Link to="/app/billing/claims?status=draft" className="card p-6 hover:shadow-md transition-shadow">
              <div className="text-sm text-gray-600">Draft claims</div>
              <div className="text-3xl font-bold text-gray-900">{query.data.draft_claims}</div>
            </Link>
            <Link to="/app/billing/claims?status=scrubbed" className="card p-6 hover:shadow-md transition-shadow">
              <div className="text-sm text-gray-600">Scrubbed claims</div>
              <div className="text-3xl font-bold text-gray-900">{query.data.scrubbed_claims}</div>
            </Link>
            <Link to="/app/billing/claims?status=submitted" className="card p-6 hover:shadow-md transition-shadow">
              <div className="text-sm text-gray-600">Submitted claims</div>
              <div className="text-3xl font-bold text-gray-900">{query.data.submitted_claims}</div>
            </Link>
            <Link to="/app/billing/denials" className="card p-6 hover:shadow-md transition-shadow">
              <div className="text-sm text-gray-600">Denied claims</div>
              <div className="text-3xl font-bold text-gray-900">{query.data.denied_claims}</div>
            </Link>
            <Link to="/app/billing/payments" className="card p-6 hover:shadow-md transition-shadow">
              <div className="text-sm text-gray-600">Payments (MTD)</div>
              <div className="text-3xl font-bold text-gray-900">{query.data.payments_count_mtd}</div>
              <div className="text-sm text-gray-600 mt-1">${query.data.payments_amount_mtd}</div>
            </Link>
            <div className="card p-6">
              <div className="text-sm text-gray-600">Gate enforcement</div>
              <div className="mt-2 text-sm text-gray-900">
                Notes lock on signature. Claims are immutable. Scrubber blocks invalid submissions.
              </div>
            </div>
          </>
        ) : (
          <div className="card p-6 text-sm text-gray-600">No billing summary.</div>
        )}
      </div>

      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link to="/app/billing/claims" className="btn-secondary text-center">
            Claims
          </Link>
          <Link to="/app/billing/payments" className="btn-secondary text-center">
            Payments
          </Link>
          <Link to="/app/billing/denials" className="btn-secondary text-center">
            Denials
          </Link>
        </div>
      </div>
    </div>
  )
}

